import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ROTULO_MODALIDADE_NO_FORMULARIO,
  ehMenorDeIdade,
  zConfirmacaoDePedido,
  zRecusaDePedido,
  type DetalheDoPedido,
} from '@arena/shared/schemas';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { siteApi } from '../api/site.js';
import {
  PRESETS_DE_PAGAMENTO,
  dinheiro,
  paraCentavos,
  telefoneFormatado,
  type ChaveDePagamento,
} from '../agenda/formatos.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Mini, Status } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { ROTULO_DO_ENCAIXE, horarioPedido, recebidoHa, situacaoDoPedido } from './formatos.js';

/**
 * `#m-pedido` — o pedido do site, e a decisão da recepção.
 *
 * "Encaixar como" oferece o que a GRADE aceita agora: as quadras livres no
 * horário do pedido, como aula experimental ou como locação. Locação numa
 * quadra da alocação fixa (documento 10) aparece desabilitada, com o
 * motivo — a mesma frase que o servidor devolveria. Tudo isso é ergonomia:
 * quem garante é a transação da confirmação.
 *
 * A aula experimental CRIA O ALUNO, e o cadastro tem os obrigatórios do
 * documento 08 que o formulário público não pede: nascimento e o aceite
 * do termo — colhidos na conversa de confirmação.
 */
export function ModalDoPedido({ id, onFechar }: { id: string; onFechar: () => void }) {
  const detalhe = useQuery({ queryKey: ['site', 'pedido', id], queryFn: () => siteApi.pedido(id) });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      marca={
        <span className="chip" style={{ background: 'var(--panel-3)' }}>
          <Icone nome="globe" />
        </span>
      }
      titulo="Pedido pelo site"
      descricao={
        detalhe.data
          ? `Recebido ${recebidoHa(detalhe.data.recebidoEm, new Date(), detalhe.data.fuso)}`
          : 'Carregando…'
      }
    >
      {detalhe.isPending ? (
        <CorpoDeModal>
          <EsqueletoDeCard linhas={4} />
        </CorpoDeModal>
      ) : detalhe.isError ? (
        <CorpoDeModal>
          <Erro
            detalhe={mensagemDoErro(detalhe.error)}
            onTentarDeNovo={() => void detalhe.refetch()}
          />
        </CorpoDeModal>
      ) : (
        <Resposta pedido={detalhe.data} onFechar={onFechar} />
      )}
    </Modal>
  );
}

function Resposta({ pedido, onFechar }: { pedido: DetalheDoPedido; onFechar: () => void }) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const agora = new Date();
  const ehLocacaoLivre = pedido.modalidade === 'Livre';

  const opcoes = pedido.quadrasLivres.flatMap((quadra) => [
    ...(ehLocacaoLivre
      ? []
      : [
          {
            valor: `Experimental|${quadra.id}`,
            rotulo: `Aula experimental · ${quadra.nome}`,
            recusa: null,
          },
        ]),
    {
      valor: `LocacaoAvulsa|${quadra.id}`,
      rotulo: `Locação avulsa · ${quadra.nome}`,
      recusa: quadra.recusaLocacao,
    },
  ]);
  const primeira = opcoes.find((o) => !o.recusa)?.valor ?? '';

  const [encaixe, setEncaixe] = useState(primeira);
  const [nascimento, setNascimento] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [aceite, setAceite] = useState(false);
  const [imagem, setImagem] = useState(false);
  const [valor, setValor] = useState(
    pedido.valorSugerido !== null ? dinheiro(pedido.valorSugerido) : '',
  );
  // Sem pagamento combinado ainda: a locação nasce "a pagar" no servidor quando há valor.
  const [pagamento, setPagamento] = useState<ChaveDePagamento | ''>('');
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [falha, setFalha] = useState<string | null>(null);

  const atualizar = () => cliente.invalidateQueries({ queryKey: ['site'] });
  const confirmar = useMutation({
    mutationFn: (dados: Parameters<typeof siteApi.confirmar>[1]) =>
      siteApi.confirmar(pedido.id, dados),
    onSuccess: () => {
      void atualizar();
      void cliente.invalidateQueries({ queryKey: ['agenda'] });
      void cliente.invalidateQueries({ queryKey: ['alunos'] });
    },
  });
  const recusar = useMutation({
    mutationFn: (texto: string) => siteApi.recusar(pedido.id, { motivo: texto }),
    onSuccess: () => void atualizar(),
  });

  const [tipo, quadraId] = encaixe.split('|') as [string, string | undefined];
  const quadra = pedido.quadrasLivres.find((q) => q.id === quadraId);
  const menor = nascimento ? ehMenorDeIdade(nascimento, pedido.hoje) : false;
  const quando = horarioPedido(pedido.inicio, agora, pedido.fuso);
  const situacao = situacaoDoPedido(pedido.situacao, pedido.motivoRecusa);

  async function aoConfirmar() {
    setErros({});
    setFalha(null);
    const preset = PRESETS_DE_PAGAMENTO.find((p) => p.chave === pagamento);
    const centavos = valor.trim() ? paraCentavos(valor) : null;
    if (valor.trim() && centavos === null) {
      setErros({ valor: 'Valor inválido — use "120" ou "120,50".' });
      return;
    }
    const corpo =
      tipo === 'Experimental'
        ? {
            encaixe: 'Experimental',
            quadraId,
            nascimento,
            responsavelFinanceiro: responsavel.trim() || undefined,
            aceiteTermo: aceite,
            consentimentoImagem: imagem,
          }
        : {
            encaixe: 'LocacaoAvulsa',
            quadraId,
            ...(centavos !== null ? { valor: centavos } : {}),
            ...(preset ? { formaPagamento: preset.forma, situacaoPagamento: preset.situacao } : {}),
          };

    const validado = zConfirmacaoDePedido.safeParse(corpo);
    if (!validado.success) {
      setErros(errosPorCampo(validado.error.issues));
      return;
    }
    try {
      await confirmar.mutateAsync(validado.data);
      avisar(
        tipo === 'Experimental'
          ? `Aula experimental confirmada na ${quadra?.nome ?? 'quadra'} — ${pedido.nome} entrou como aluno experimental.`
          : `Locação confirmada na ${quadra?.nome ?? 'quadra'}.`,
      );
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setFalha(mensagemDoErro(erro));
    }
  }

  async function aoRecusar() {
    setFalha(null);
    const validado = zRecusaDePedido.safeParse({ motivo });
    if (!validado.success) {
      setErros(errosPorCampo(validado.error.issues));
      return;
    }
    try {
      await recusar.mutateAsync(validado.data.motivo);
      avisar('Pedido recusado. O horário continua livre na agenda.');
      onFechar();
    } catch (erro) {
      setFalha(mensagemDoErro(erro));
    }
  }

  const pendente = pedido.situacao === 'Pendente';

  return (
    <>
      <CorpoDeModal>
        <Mini
          itens={[
            { valor: pedido.nome, rotulo: 'nome informado' },
            {
              valor: ROTULO_MODALIDADE_NO_FORMULARIO[pedido.modalidade],
              rotulo: 'modalidade',
            },
            { valor: quando, rotulo: 'horário pedido' },
          ]}
        />
        <Campo rotulo="Telefone">
          <input value={pedido.telefone ? telefoneFormatado(pedido.telefone) : '—'} readOnly />
        </Campo>
        {pedido.mensagem && (
          <Campo rotulo="Mensagem">
            <textarea rows={2} value={pedido.mensagem} readOnly />
          </Campo>
        )}

        {!pendente && (
          <p className="sub">
            <Status situacao={situacao.situacao}>{situacao.rotulo}</Status>{' '}
            {pedido.encaixe
              ? `Encaixado como ${ROTULO_DO_ENCAIXE[pedido.encaixe].toLowerCase()}.`
              : ''}
          </p>
        )}

        {pendente && !recusando && (
          <>
            {opcoes.length === 0 ? (
              <div className="aviso conflito" role="alert">
                <Icone nome="info" />
                <div>
                  <b>O horário deste pedido está ocupado nas quadras.</b> Recuse com o motivo, ou
                  ligue para combinar outro horário.
                </div>
              </div>
            ) : (
              <Campo
                rotulo="Encaixar como"
                erro={erros['quadraId'] ?? erros['encaixe']}
                ajuda={opcoes.find((o) => o.valor === encaixe)?.recusa ?? undefined}
              >
                <select value={encaixe} onChange={(e) => setEncaixe(e.target.value)}>
                  {opcoes.map((o) => (
                    <option key={o.valor} value={o.valor} disabled={Boolean(o.recusa)}>
                      {o.rotulo}
                      {o.recusa ? ' — reservada à turma' : ''}
                    </option>
                  ))}
                </select>
              </Campo>
            )}

            {tipo === 'Experimental' && opcoes.length > 0 && (
              <>
                <Campo
                  rotulo="Data de nascimento *"
                  erro={erros['nascimento']}
                  ajuda="O aluno experimental entra no cadastro com origem Site."
                >
                  <input
                    type="date"
                    value={nascimento}
                    onChange={(e) => setNascimento(e.target.value)}
                  />
                </Campo>
                {menor && (
                  <Campo rotulo="Responsável financeiro *" erro={erros['responsavelFinanceiro']}>
                    <input value={responsavel} onChange={(e) => setResponsavel(e.target.value)} />
                  </Campo>
                )}
                <label className="consent">
                  <input
                    className="ck"
                    type="checkbox"
                    checked={aceite}
                    onChange={(e) => setAceite(e.target.checked)}
                  />
                  <span>
                    Aceite do termo de uso e da política de privacidade *
                    <small>
                      Colhido na conversa · versão {pedido.versaoTermoVigente}
                      {erros['aceiteTermo'] ? ` — ${erros['aceiteTermo']}` : ''}
                    </small>
                  </span>
                </label>
                <label className="consent">
                  <input
                    className="ck"
                    type="checkbox"
                    checked={imagem}
                    onChange={(e) => setImagem(e.target.checked)}
                  />
                  <span>
                    Consentimento de uso de imagem
                    <small>Separado do termo e revogável a qualquer momento</small>
                  </span>
                </label>
              </>
            )}

            {tipo === 'LocacaoAvulsa' && opcoes.length > 0 && (
              <div className="form f2">
                <Campo
                  rotulo="Valor"
                  erro={erros['valor']}
                  ajuda={
                    pedido.descontoPercentual
                      ? `Com ${pedido.descontoPercentual}% da promoção da faixa`
                      : undefined
                  }
                >
                  <input
                    value={valor}
                    onChange={(e) => setValor(e.target.value)}
                    placeholder="R$ 0,00"
                  />
                </Campo>
                <Campo rotulo="Pagamento">
                  <select
                    value={pagamento}
                    onChange={(e) => setPagamento(e.target.value as ChaveDePagamento | '')}
                  >
                    <option value="">Não informado</option>
                    {PRESETS_DE_PAGAMENTO.map((p) => (
                      <option key={p.chave} value={p.chave}>
                        {p.rotulo}
                      </option>
                    ))}
                  </select>
                </Campo>
              </div>
            )}

            {opcoes.length > 0 && (
              <p className="sub">
                Confirmar reserva a quadra e tira o horário da grade. Avisar{' '}
                {pedido.nome.split(' ')[0]} é com você: o produto não envia mensagem nenhuma — o
                telefone está aí em cima.
              </p>
            )}
          </>
        )}

        {pendente && recusando && (
          <Campo rotulo="Motivo da recusa *" erro={erros['motivo']}>
            <input
              autoFocus
              placeholder="Sem quadra nesse horário"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </Campo>
        )}

        {falha && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{falha}</div>
          </div>
        )}
      </CorpoDeModal>

      <footer className="m-foot">
        {!pendente ? (
          <Botao variante="fantasma" onClick={onFechar}>
            Fechar
          </Botao>
        ) : recusando ? (
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(false)}>
              Voltar
            </Botao>
            <Botao onClick={() => void aoRecusar()} disabled={recusar.isPending}>
              {recusar.isPending ? 'Recusando…' : 'Recusar pedido'}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(true)}>
              Recusar
            </Botao>
            <Botao
              onClick={() => void aoConfirmar()}
              disabled={confirmar.isPending || opcoes.length === 0 || !primeira}
            >
              {confirmar.isPending ? 'Confirmando…' : 'Confirmar reserva'}
            </Botao>
          </>
        )}
      </footer>
    </>
  );
}
